import React from 'react'
import { CheckCircle, FileText, ClipboardCheck } from 'lucide-react'

/**
 * Visual da hero: screenshot do banco em moldura de browser + chips
 * flutuantes decorativos sugerindo a transformação banco → prova pronta.
 * Animações 100% CSS (transform/opacity) gated por motion-safe.
 * A imagem é candidata a LCP no desktop — nunca animar opacity nela.
 */
const HeroMockup: React.FC = () => {
  return (
    <div className="relative mx-auto mt-8 max-w-lg lg:mt-0 lg:max-w-none">
      {/* Anotação manuscrita */}
      <span
        aria-hidden="true"
        className="absolute -top-9 right-8 hidden md:block font-hand text-xl text-terra-500 -rotate-3 motion-safe:animate-hero-rise-3"
      >
        do banco pra prova em minutos ↓
      </span>

      {/* Moldura de browser */}
      <div className="bg-surface dark:bg-ink-700 border border-cream-400 dark:border-ink-500 rounded-caderno-sm shadow-caderno-xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-cream-300 dark:border-ink-500">
          <span className="w-2.5 h-2.5 rounded-full bg-terra-300" aria-hidden="true" />
          <span className="w-2.5 h-2.5 rounded-full bg-ochre-300" aria-hidden="true" />
          <span className="w-2.5 h-2.5 rounded-full bg-moss-300" aria-hidden="true" />
          <span className="ml-2 font-mono text-caderno-xs text-ink-400 dark:text-ink-300 bg-cream-100 dark:bg-ink-900 rounded-full px-3 py-0.5">
            atividadepronta.com.br/banco
          </span>
        </div>
        <picture>
          <source
            type="image/avif"
            srcSet="/landing/hero/hero-banco-640.avif 640w, /landing/hero/hero-banco-960.avif 960w, /landing/hero/hero-banco-1280.avif 1280w"
            sizes="(min-width: 1024px) 44vw, 92vw"
          />
          <source
            type="image/webp"
            srcSet="/landing/hero/hero-banco-640.webp 640w, /landing/hero/hero-banco-960.webp 960w, /landing/hero/hero-banco-1280.webp 1280w"
            sizes="(min-width: 1024px) 44vw, 92vw"
          />
          <img
            src="/landing/hero/hero-banco-960.webp"
            width={1440}
            height={900}
            alt="Tela do banco de questões: filtros por disciplina, série e BNCC e questões prontas para adicionar à prova"
            className="block w-full h-auto"
            // React 18 não tipa/reconhece fetchPriority camelCase (só React 19);
            // o atributo DOM precisa ser lowercase.
            {...({ fetchpriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>)}
            loading="eager"
            decoding="async"
          />
        </picture>
      </div>

      {/* Chips flutuantes decorativos */}
      <div aria-hidden="true" className="absolute top-16 -left-3 sm:-left-8 motion-safe:animate-hero-rise-3">
        <div className="motion-safe:animate-hero-float w-44 bg-surface dark:bg-ink-700 border border-cream-400 dark:border-ink-500 rounded-caderno-sm shadow-caderno-lg p-3">
          <p className="font-mono text-caderno-xs uppercase tracking-widest text-ink-400 dark:text-ink-300 mb-1">
            MAT · 7º ano
          </p>
          <p className="text-xs text-ink-900 dark:text-cream-50 leading-snug mb-2">
            Dona Marta dividiu a pizza em 8 partes iguais…
          </p>
          <p className="font-mono text-caderno-xs text-ink-400 dark:text-ink-300">
            ( ) 3/8&ensp;(•) 5/8&ensp;( ) 8/3
          </p>
        </div>
      </div>

      <div aria-hidden="true" className="absolute top-10 -right-2 sm:-right-6 motion-safe:animate-hero-rise-3">
        <div className="motion-safe:animate-hero-float-slow inline-flex items-center gap-2 bg-surface dark:bg-ink-700 border border-cream-400 dark:border-ink-500 rounded-full shadow-caderno-lg px-3 py-1.5">
          <CheckCircle className="w-4 h-4 text-moss-500" />
          <span className="font-mono text-caderno-xs text-ink-500 dark:text-cream-300">BNCC EF07MA09</span>
        </div>
      </div>

      <div aria-hidden="true" className="absolute -bottom-5 left-6 motion-safe:animate-hero-rise-3">
        <div className="motion-safe:animate-hero-float-slow inline-flex items-center gap-2 bg-surface dark:bg-ink-700 border border-cream-400 dark:border-ink-500 rounded-full shadow-caderno-lg px-3 py-1.5">
          <FileText className="w-4 h-4 text-terra-500" />
          <span className="text-xs font-medium text-ink-500 dark:text-cream-300">PDF pronto pra imprimir</span>
        </div>
      </div>

      <div aria-hidden="true" className="absolute -bottom-4 -right-2 motion-safe:animate-hero-rise-3">
        <div className="motion-safe:animate-hero-float inline-flex items-center gap-2 bg-surface dark:bg-ink-700 border border-cream-400 dark:border-ink-500 rounded-full shadow-caderno-lg px-3 py-1.5">
          <ClipboardCheck className="w-4 h-4 text-moss-500" />
          <span className="text-xs font-medium text-ink-500 dark:text-cream-300">Gabarito incluso</span>
        </div>
      </div>
    </div>
  )
}

export default HeroMockup
